import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Button,
  CardHeader,
  CardBody,
  CardTitle,
  CardText,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
  Label,
  Input,
  CardFooter,
  Container
} from "reactstrap";

import classnames from "classnames";
import CodeExecutor2 from "../CodeExecutor/CodeExecutor2.js";
import { useLesson } from "../../hooks/lessonFramework.js";
import { useUserAuth } from "../../context/UserContext.js";
import { useRegexTest } from "../../hooks/RegexTest.js";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChalkboard, faSquareXmark, faSquareCheck, faFlask } from "@fortawesome/free-solid-svg-icons";
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import Highlight, { defaultProps } from "prism-react-renderer";
import github from "prism-react-renderer/themes/github";
import ReactMarkdown from "react-markdown";
import { requestUserLessonContent, setUserLessonContent, requestAnonSetCourse, setLessonAsCompleted } from "../../services/userService.js";
import { requestInitializeLesson, requestAIInitializePracticeProblems, requestAIPredeterminedProblems } from "../../services/RobotAIServices.js";
import { useTranslation } from "react-i18next";
import logoVideo from "../../assets/videos/loadingCircleBlackOnWhite1080.mp4";


const LearningHubComponent = ({ setAiPageContext, content, onLoadLesson, practiceProblems, onPracticeProblems, course, lessonID, lessonTitle, setallCheckboxesCompleted, setCurrentCode, customeRegex = null, aiOpenHelpRef, setCurrentOuput }) => {
  // const lessonID = "JS107";
  const [initialTitle, setInitialTitle] = useState();
  const [isCreatingContent, setIsCreatingContent] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("Generating content…");

  const { user, signInAnonUser } = useUserAuth();
  const { t, i18n } = useTranslation();
  const { RegexCheckProblems } = useRegexTest();

  const [initialLesson, setInitialLesson] = useState(null);
  const [initialLessonCode, setInitialLessonCode] = useState();

  const [animatedSections, setAnimatedSections] = useState([]);
  const [activeTab, setActiveTab] = useState("1");


  const [checkedStates, setCheckedStates] = useState({});
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [editorCode, setEditorCode] = useState("");


  const [adminResolve, setAdminResolve] = useState(false);


  useEffect(() => {
    setInitialTitle(lessonTitle);
    requestLessonFunction(lessonTitle);
  }, []);



  useEffect(() => {
    if (initialLesson != content) {
      runWritingAnimation()
      setInitialLesson(content);
    }
  }, [content]);


  useEffect(() => {
    if (adminResolve) {
      executeTests("", "", true)
    }
  }, [adminResolve]);


  const getFirstUnfinishedProblemIndex = (problems) => {
    if (!problems || !Array.isArray(problems)) return 0;

    const index = problems.findIndex((problem) => problem.problemCompleted !== true);
    return index === -1 ? 0 : index;
  };

  const loadProblemIntoEditor = (problems, index) => {
    if (!problems || !problems[index]) {
      setCurrentProblemIndex(0);
      setEditorCode("");
      setInitialLessonCode("");
      return;
    }

    const starterCode = problems[index].initialCode ?? "";
    setCurrentProblemIndex(index);
    setEditorCode(starterCode);
    setInitialLessonCode(starterCode);
  };


  const requestLessonFunction = async (title) => {

    let currentUser = user;


    // IF THERE IS NO USER IT WILL SAVE THE DATA IN THE SESSION
    if (!currentUser) {
      currentUser = await signInAnonUser();
      await requestAnonSetCourse("JavaScript", currentUser.uid, currentUser.accessToken)
    }


    // REQUEST USER CURRENT CONTENT 
    const responseContent = await requestUserLessonContent(currentUser, lessonID);


    // IF USER DOES NOT HAVE LESSON CONTENT, IT WILL CREATE/INITIALIZ THE CONTENT. IF IT DOES HAVE IT, IT WILL PASS THE CONTENT
    if (responseContent.content != null) {
      // USER DOES HAVE LESSON CONTENT

      // PASSES THE CURRENT CONTENT
      setInitialLesson(responseContent.content)
      await onLoadLesson(responseContent.content);
      onPracticeProblems(responseContent.problemsContent);

      // Dynamically generate checkbox state keys
      const initialCheckboxStates = {};
      for (let i = 0; i < responseContent.problemsContent.problems.length; i++) {
        initialCheckboxStates[`checkboxP${i + 1}`] = responseContent.problemsContent.problems[i].problemCompleted;
      }
      setCheckedStates(initialCheckboxStates);
      const allPassed = Object.values(initialCheckboxStates).every(value => value === true);

      if (allPassed && responseContent.lessonCompleted) {
        setallCheckboxesCompleted(true);
      }

      const loadedProblems = responseContent.problemsContent?.problems || [];
      const firstUnfinishedIndex = getFirstUnfinishedProblemIndex(loadedProblems);
      loadProblemIntoEditor(loadedProblems, firstUnfinishedIndex);


    } else {
      // USER DOES NOT HAVE LESSON CONTENT

      let args
      let args2;
      let initialLessonResult;

      setIsCreatingContent(true);

      // CREATING LESSON CONTENT
      const res = await requestInitializeLesson(course, title, i18n.language);


      if (res.function_call?.name === "writeInitialLesson") {
        args = JSON.parse(res.function_call.arguments);
        initialLessonResult = {
          role: "function",
          name: "writeInitialLesson",
          content: JSON.stringify(args)
        };
      }

      // commented code bellow is for AI to create practice problems NOT independently since the prompt is not good enough or AI is not smart enough yet.
      // const res2 = await requestAIPredeterminedProblems(title, initialLessonResult);
      // commented code bellow is for AI to create practice problems independently. For right now it can not do it. It needs more testing. 

      // CREATING PRACTICE PROBLEMS
      const res2 = await requestAIInitializePracticeProblems(course, title, initialLessonResult, i18n.language);

      let numberOfProblems = 0;
      if (res2.function_call?.name === "generatePracticeProblems") {
        args2 = JSON.parse(res2.function_call.arguments);
        numberOfProblems = args2.problems.length;
      }

      // Dynamically generate checkbox state keys
      const initialCheckboxStates = {};
      for (let i = 0; i < numberOfProblems; i++) {
        initialCheckboxStates[`checkboxP${i + 1}`] = false;
      }

      setLoadingMsg("Almost ready…");

      const generatedProblems = args2?.problems || [];
      const firstUnfinishedIndex = getFirstUnfinishedProblemIndex(generatedProblems);
      loadProblemIntoEditor(generatedProblems, firstUnfinishedIndex);

      setCheckedStates(initialCheckboxStates);
      await setUserLessonContent(currentUser, lessonID, args, args2)

      // PASS THE CONTENET TO THE AI FOR REFERENCE
      await onLoadLesson(args);
      await onPracticeProblems(args2);

      setIsCreatingContent(false);

    }
  }


  // when change lesson this useEffect creates a typen effect to update new content
  const runWritingAnimation = () => {

    if (!content) return;
    // setLessonContent(content);
    setAnimatedSections([]); // Reset displayed content
    const animateSection = (index = 0) => {
      if (index >= content.sections.length) return;
      const section = content.sections[index];
      let i = 0;
      const interval = setInterval(() => {
        i++;
        setAnimatedSections((prev) => {
          const updated = [...prev];
          const animatedContent = section.content?.slice(0, i) ?? null;
          updated[index] = {
            ...section,
            content: animatedContent
          };
          return updated;
        });
        if (!section.content || i >= section.content.length) {
          clearInterval(interval);
          setTimeout(() => animateSection(index + 1), 50); // small delay before next section
        }
      }, 3); // typing speed (ms per character)
    };
    animateSection(0); // start from first section

  };



  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };



  // Function to update the state of specific checkboxes
  const updateCheckboxState = (updates) => {
    setCheckedStates((prevState) => ({
      ...prevState,
      ...updates,
    }));
  };



  const executeTests = async (code, output, adminResolve = false) => {
    let newCheckedStates;

    if (customeRegex) {
      newCheckedStates = await customeRegex(
        code,
        output,
        checkedStates,
        user,
        lessonID,
        practiceProblems,
        adminResolve
      );
    } else {
      newCheckedStates = await RegexCheckProblems(
        code,
        output,
        checkedStates,
        user,
        lessonID,
        practiceProblems,
        adminResolve
      );
    }

    await updateCheckboxState(newCheckedStates);

    const allPassed = Object.values(newCheckedStates).every(value => value === true);

    if (allPassed) {
      await setLessonAsCompleted(lessonID, user.uid);
      setallCheckboxesCompleted(true);
      return;
    }

    const problems = practiceProblems?.problems || [];

    const nextUnfinishedIndex = problems.findIndex((problem, index) => {
      const checkboxKey = `checkboxP${index + 1}`;
      return newCheckedStates[checkboxKey] !== true;
    });

    if (nextUnfinishedIndex !== -1 && nextUnfinishedIndex !== currentProblemIndex) {
      loadProblemIntoEditor(problems, nextUnfinishedIndex);
    }
  };




  return (
    <>

      <Container fluid className=" pr-0 pl-0   fullWitdthHeight ">
        {isCreatingContent && (
          <div className="courses-loading-overlay" role="alert" aria-busy="true" aria-live="polite">
            <div className="courses-loading-card">
              <video
                className="courses-loading-video"
                src={logoVideo}
                autoPlay
                loop
                muted
                playsInline
                draggable={false}
                aria-hidden="true"
              />
              {/* <img
                className="courses-loading-img"
                src={require("assets/img/theme/loading.jpg")}
                alt="Loading..."
              /> */}
              <div className="courses-loading-text">{loadingMsg}</div>
            </div>
          </div>
        )}
        {initialLessonCode !== null && (
          <CodeExecutor2 languageCode="javascript" initialCode={editorCode} runTestCase={executeTests} setCurrentCode={setCurrentCode} setCurrentOuput={setCurrentOuput} >
            <Card className="fullWitdthHeight">
              <CardHeader className=" lesson-tabs-headers ">
                {/* {initialTitle} */}
                <Nav tabs className="justify-content-center nav-tabs-bottom ">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => toggle("1")}
                    >
                      <FontAwesomeIcon icon={faChalkboard} size="lg" />
                      &nbsp;
                      {t("lesson.lesson")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "2" })}
                      onClick={() => toggle("2")}
                    >
                      <FontAwesomeIcon icon={faFlask} size="lg" />
                      &nbsp;
                      {t("lesson.learningLab")}
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardHeader>
              <TabContent className="fullWitdthHeight overflow-auto " activeTab={activeTab}>
                <TabPane className="fullWitdthHeight" tabId="1">
                  <CardBody>
                    <CardTitle tag="h2">{initialTitle}</CardTitle>
                    {(animatedSections.length ? animatedSections : content?.sections)?.map((section, index) => (
                      <div key={index}>
                        <CardTitle tag="h5">{section.title}</CardTitle>
                        {/* Render content as Markdown */}
                        <CardText>
                          <ReactMarkdown>{section.content}</ReactMarkdown>
                        </CardText>
                        {section.code && (
                          <CardText>
                            <Highlight {...defaultProps} theme={github} code={section.code} language="javascript">
                              {({ style, tokens, getLineProps, getTokenProps }) => (
                                <pre style={{ ...style, padding: "15px", borderRadius: "5px", overflowX: "auto" }}>
                                  {tokens.map((line, i) => (
                                    <div key={i} {...getLineProps({ line, key: i })}>
                                      {line.map((token, key) => (
                                        <span key={key} {...getTokenProps({ token, key })} />
                                      ))}
                                    </div>
                                  ))}
                                </pre>
                              )}
                            </Highlight>
                          </CardText>
                        )}
                      </div>
                    ))}
                  </CardBody>
                </TabPane>
                <TabPane tabId="2">
                  <CardBody>
                    {/* CHECKBOXS */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "1rem"
                      }}
                    >
                      <h4 style={{ margin: 0 }}>{t("lesson.tryit")}</h4>

                      <Button className="btn-1" color="primary" outline type="button" onClick={() => aiOpenHelpRef.current?.()} >
                        {t("lesson.needHelp")}
                      </Button>
                    </div>
                    <div className="">
                      <FormGroup >
                        {practiceProblems?.problems.map((section, index) => {
                          const checkboxKey = `checkboxP${index + 1}`;
                          return (
                            // <div key={index} className="checkBoxSeparator">
                            <div
                              key={index}
                              className="checkBoxSeparator"
                              style={{
                                padding: "8px",
                                borderRadius: "6px",
                                backgroundColor: index === currentProblemIndex ? "#f5f5f5" : "transparent",
                                fontWeight: index === currentProblemIndex ? "600" : "400"
                              }}
                            >
                              <Label check>
                                {checkedStates[checkboxKey] === true ? (
                                  <span style={{ fontSize: '1.4rem', color: '#90ee90' }}><FontAwesomeIcon icon={faSquareCheck} /></span>
                                ) : checkedStates[checkboxKey] === 'fail' ? (

                                  <span style={{ fontSize: '1.4rem', color: 'red' }}><FontAwesomeIcon icon={faSquareXmark} /></span>

                                ) : (

                                  <span style={{ fontSize: '1.4rem', }}><FontAwesomeIcon icon={faSquare} /></span>

                                )}
                                &nbsp; {section.instruction}
                              </Label>
                            </div>
                          );
                        })}
                      </FormGroup>

                    </div>

                    {/* CHECKBOXS */}

                    {/* <Button
                      className="mr-2  custom-purple"
                      href="#pablo"
                      onClick={() => setAdminResolve(true)}
                    >
                      <span className="btn-inner--text">TESTING RESOLVE</span>
                    </Button> */}
                  </CardBody>
                </TabPane>
              </TabContent>
              <CardFooter className=" lesson-footer">

                {/* <Nav tabs className="justify-content-center my-nav-tabs ">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => toggle("1")}
                    >
                      <FontAwesomeIcon icon={faChalkboard} size="lg" />
                      &nbsp;
                      Lesson
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "2" })}
                      onClick={() => toggle("2")}
                    >
                      <FontAwesomeIcon icon={faFlask} size="lg" />
                      &nbsp;
                      Learning Lab
                    </NavLink>
                  </NavItem>
                </Nav> */}
              </CardFooter>
            </Card>
          </CodeExecutor2>
        )}
      </Container>

    </>
  );
};


export default LearningHubComponent;